import { Component, EventEmitter, Output, ViewChild, AfterViewInit, OnInit, OnDestroy, Input, SimpleChanges, HostListener, ElementRef, QueryList, ViewChildren, OnChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTreeModule, MatTree } from '@angular/material/tree';
import { LibraryItemHeaderComponent } from '../../../Common/library-item-header/library-item-header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LIBRARY_TABS, NodeType } from '../../../../models/enum';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonDataService } from '../../../../services/common-data.service';
import { NameValidationService } from '../../../../services/name-validation.service';
import { Subject, takeUntil } from 'rxjs';
import { ProjectViewLayoutConfiguration } from '../../../../models/common/PreferanceResponse';
import { ControllerState, DesigoCCState, DesigoOpticState, DesigoOpticType, SQWebProject, SQWebSystem, TypicalOfSystemHelper } from '../../../../models/dtos';
import { ProjectTreeNode } from '../../../../models/projectTreeNode';
import { CONSTANTS } from '../../../../Constants/Constants';
import { SolCalculationsService } from '../../../../services/operations/sol-calculations.service';
import { LoadingSpinnerService } from '../../../../services/common/loading-spinner.service';

@Component({
  selector: 'app-project-view-tree',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTreeModule,
    LibraryItemHeaderComponent,
    TranslatePipe,
    MatTooltipModule
  ],
  templateUrl: './project-view-tree.component.html',
  styleUrl: './project-view-tree.component.scss'
})
export class ProjectViewTreeComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Output() closeProjectViewLibrary = new EventEmitter<LIBRARY_TABS>();
  @Output() unpinPreviewLibrary = new EventEmitter<LIBRARY_TABS>();
  @ViewChild('projectTree') projectTree!: MatTree<ProjectTreeNode>;
  @Input() projectViewLayout: ProjectViewLayoutConfiguration;
  @Input() isEstimateTab: boolean = false;

  @ViewChildren('nodeInput') nodeInputs!: QueryList<ElementRef<HTMLInputElement>>;
  private currentlyEditingNode: ProjectTreeNode | null = null;
  // Column width properties
  nameColumnWidth: string = 'calc(100% - 60px)';
  statusColumnWidth: string = '30px';
  actionsColumnWidth: string = '30px';

  areasData: SQWebProject = new SQWebProject();
  isAllTypicalOffExpanded: boolean = true;
  treeData: ProjectTreeNode[] = [];
  isReadOnlyMode: boolean = false;
  destroy$: Subject<void> = new Subject<void>();
  nodeType = NodeType;
  typicalOfHelpers: Record<string, TypicalOfSystemHelper> = {};

  private isCalculatingVisibility = false;
  private previousNodeNames: Map<ProjectTreeNode, string> = new Map();

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly calculationsService: SolCalculationsService,
    private readonly nameValidationService: NameValidationService,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly translateService: TranslateService
  ) { }

  childrenAccessor(node: ProjectTreeNode) {
    return (node.children) ?? [];
  }

  hasChild(_: number, node: ProjectTreeNode): boolean {
    return !!node.children && node.children.length > 0;
  }

  isControllerOrDesigoOptic(_: number, node: ProjectTreeNode): boolean {
    return node.nodeType === NodeType.Controller || node.nodeType === NodeType.DesigoOptic || node.nodeType === NodeType.Panel;
  }

  getControllerInfo(node: ProjectTreeNode): string {
    const controller = node.children?.find(child => child.nodeType === NodeType.Controller || child.nodeType === NodeType.Panel);
    if (!controller) {
      return CONSTANTS.EMPTY_STRING;
    }
    switch (controller.controllerState) {
      case ControllerState.HasUnsizedController:
        return 'assets/images/Controller.JPG';
      case ControllerState.HasSizedController:
        return 'assets/images/Controllersizedok.JPG';
      case ControllerState.HasESPowerStudySizing:
      case ControllerState.HasESStartUpSizing:
      case ControllerState.HasBothESSizing:
        return 'assets/images/EsSized.gif';
      case ControllerState.HasUnsizedES:
        return 'assets/images/ESUnSized.gif';
      default:
        return CONSTANTS.EMPTY_STRING;
    }
  };

  getLeafNodeIcon(node: ProjectTreeNode): string {
    switch (node.nodeType) {
      case NodeType.Area:
        return 'assets/images/Area.png';
      default:
        if (node.hasTypicalOf) {
          return this.isAllTypicalOffExpanded
            ? 'assets/images/Typical.JPG'
            : 'assets/images/ShowTypical.gif';
        }
        return node.isSystemES
          ? 'assets/images/ESSystemFlag.gif'
          : 'assets/images/System.png';
    }
  }

  getParentNodeIcon(node: ProjectTreeNode): string {
    switch (node.nodeType) {
      case NodeType.Area:
        return 'assets/images/Area.png';
      default:
        if (node.hasTypicalOf) {
          return this.isAllTypicalOffExpanded
            ? 'assets/images/Typical.JPG'
            : 'assets/images/ShowTypical.gif';
        }
        return 'assets/images/Project or Estimate Level.png';
    }
  }

  getDesigoOpticInfo(node: ProjectTreeNode): string {
    const desigoOptic = node.children?.find(child => child.nodeType === NodeType.DesigoOptic);
    if (!desigoOptic) {
      return CONSTANTS.EMPTY_STRING;
    }
    switch (desigoOptic.desigoOpticState) {
      case DesigoOpticState.HasSizedDesigoOptic:
        if (!desigoOptic.desigoOpticType || desigoOptic.desigoOpticType === DesigoOpticType.New) {
          return 'assets/images/DesigoOpticSized.png';
        } else if (desigoOptic.desigoOpticType === DesigoOpticType.Distributed) {
          return 'assets/images/DesigoOpticDistributedSized.png';
        }
        break;
      case DesigoOpticState.HasUnsizedDesigoOptic:
        if (!desigoOptic.desigoOpticType || desigoOptic.desigoOpticType === DesigoOpticType.New) {
          return 'assets/images/DesigoOpticChanged.png';
        } else if (desigoOptic.desigoOpticType === DesigoOpticType.Distributed) {
          return 'assets/images/DesigoOpticDistributedChanged.png';
        }
        break;
      default:
        return CONSTANTS.EMPTY_STRING;
    }
    return CONSTANTS.EMPTY_STRING;
  }

  getDesigoCCInfo(node: ProjectTreeNode): string {
    if (node.nodeType == NodeType.System && node.areaControllerState > CONSTANTS.ZERO) {
      if (node.areaControllerState === DesigoCCState.HasSizedDCC) {
        return 'assets/images/Controllersizedok.JPG';
      } else if (node.areaControllerState === DesigoCCState.HasUnsizedDCC) {
        return 'assets/images/Controller.JPG';
      } else if (node.areaControllerState === DesigoCCState.HasVMSSystem) {
        return 'assets/images/VMSSystemFlag.gif';
      }
    }
    return CONSTANTS.EMPTY_STRING;
  }

  ngOnInit() {
    this.generateProjectViewTree();
    this.commonDataService.readOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(disabled => {
        this.isReadOnlyMode = disabled;
      });

    this.commonDataService.estimateProjectNameAndDescChanged$.pipe(takeUntil(this.destroy$)).subscribe((estimateProjectData) => {
      if (estimateProjectData?.estimateName !== undefined && this.treeData.length > 0) {
        this.treeData[0].itemName = estimateProjectData.estimateName ?? '';
        this.areasData.itemName = estimateProjectData.estimateName ?? '';
      }
    });


  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['projectViewLayout']) {
      this.assignProjectViewWidth();
    }
  }

  ngAfterViewInit() {
    // Set initial expansion state based on isExpanded flag
    this.applyInitialExpansionState();
  }

  private applyInitialExpansionState() {
    this.treeData.forEach(node => {
      this.setNodeExpansionState(node);
    });
  }

  private setNodeExpansionState(node: ProjectTreeNode) {
    if (node && node.children && node.children.length > 0) {
      if (node.isExpanded) {
        this.projectTree.expand(node);
      } else {
        this.projectTree.collapse(node);
      }
      node.children.forEach(child => this.setNodeExpansionState(child));
    }
  }

  private assignProjectViewWidth() {
    if (!this.projectViewLayout) {
      return;
    }
    const nameColumn = this.projectViewLayout.projectViewTemplate;
    const statusColumn = this.projectViewLayout.controllerIconTemplate;
    const actionsColumn = this.projectViewLayout.hideShowTemplate;
    this.setColumnWidths(nameColumn, statusColumn, actionsColumn);
  }

  private generateProjectViewTree() {
    this.commonDataService.projectObjectDataAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(dataAvailable => {
        if (dataAvailable) {
          this.areasData = this.commonDataService.getSQWebProject();
          this.typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
          const isRootVisible = this.areasData.isItemVisible ?? true;
          this.commonDataService.setRootNodeHidden(!isRootVisible);
          this.treeData = [
            {
              itemName: this.areasData.itemName,
              isExpanded: this.areasData.isExpanded ?? true,
              isInEditMode: false,
              isItemVisible: isRootVisible,
              children: this.areasData.areas as ProjectTreeNode[],
              hasTypicalOf: false,
              isSystemES: false,
            }
          ];
        }
        else {
          this.commonDataService.setRootNodeHidden(false);
          this.treeData = [];
        }
        this.assignTypicalOfCounts(this.treeData);
        this.collapseTypicalOff();
      });
  }

  private assignTypicalOfCounts(nodes: ProjectTreeNode[]): void {
    nodes.forEach(node => {
      this.assignTypicalOfCount(node);
      if (node.children?.length) {
        this.assignTypicalOfCounts(node.children);
      }
    });
  }

  private assignTypicalOfCount(node: ProjectTreeNode): void {
    if (!node.hasTypicalOf) {
      return;
    }

    const helperUniqueId = node.systemTypicalOfHelperUniqueId;
    if (!helperUniqueId) {
      node.typicalOfCount = CONSTANTS.ONE;
      return;
    }

    const helper = this.typicalOfHelpers[helperUniqueId];

    if (!helper) {
      node.typicalOfCount = CONSTANTS.ONE;
      return;
    }

    node.typicalOfCount = helper.typicalOfSystems?.length ?? CONSTANTS.ONE;
  }

  toggleNodeExpansion(node: ProjectTreeNode) {
    if (this.projectTree.isExpanded(node)) {
      this.projectTree.collapse(node);
      node.isExpanded = false;
    } else {
      this.projectTree.expand(node);
      node.isExpanded = true;
    }
  }

  closeLibrary() {
    this.closeProjectViewLibrary.emit(LIBRARY_TABS.ProjectView);
  }

  unpinLibrary() {
    this.unpinPreviewLibrary.emit(LIBRARY_TABS.ProjectView);
  }

  onNodeDoubleClick(node: ProjectTreeNode) {
    // Disable edit mode in read-only mode or if item is not visible
    if (this.isReadOnlyMode || !node.isItemVisible) {
      return;
    }
    if (this.currentlyEditingNode) {
      this.currentlyEditingNode.isInEditMode = false;
    }
    // Store the previous name before entering edit mode
    this.previousNodeNames.set(node, node.itemName);
    node.isInEditMode = true;
    this.currentlyEditingNode = node;
  }

  saveNodeName(node: ProjectTreeNode, newName?: string) {
    if (this.isReadOnlyMode) {
      node.isInEditMode = false;
      this.currentlyEditingNode = null;
      return;
    }

    if (newName && node) {
      const previousName = this.previousNodeNames.get(node) || node.itemName;
      // Validate the new name using the reusable validation service
      const validatedName = this.nameValidationService.validateAndCleanName(newName, previousName, 'estimateName');
      node.itemName = validatedName;
    }
    node.isInEditMode = false;
    this.currentlyEditingNode = null;
    this.commonDataService.isSaveEnabled = true;
    if (node === this.treeData[0]) {
      const project = this.commonDataService.getSQWebProject();
      if (project) {
        project.itemName = node.itemName;
        this.commonDataService.patchProject(project);
      }
    }
  }

  async toggleNodeVisibility(node: ProjectTreeNode): Promise<void> {
    // Disable visibility toggle in read-only mode
    if (this.isReadOnlyMode || this.isCalculatingVisibility) {
      return;
    }
    // Check if any parent node up to root is invisible
    let parentVisible = true;
    let currentParent = this.findParentNode(node);
    while (currentParent && parentVisible) {
      if (!currentParent.isItemVisible) {
        parentVisible = false;
      }
      currentParent = this.findParentNode(currentParent);
    }
    if (!parentVisible) {
      return;
    }

    this.isCalculatingVisibility = true;
    this.loadingSpinnerService.updateMessage(this.translateService.instant('NGX_SPINNER_CALCULATING'));
    this.loadingSpinnerService.show(CONSTANTS.DASHBOARD_SPINNER);
    try {
      // Keep the existing delay so the overlay paints before the hierarchy work starts.
      await new Promise<void>(resolve => setTimeout(resolve, 300));
      this.applyNodeVisibility(node);
      await this.calculationsService.calculateCostsAndSellPricesWithCollections();
    } finally {
      this.isCalculatingVisibility = false;
      this.loadingSpinnerService.updateMessage('');
      this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
    }
  }

  private applyNodeVisibility(node: ProjectTreeNode): void {
    node.isItemVisible = !node.isItemVisible;
    this.commonDataService.isSaveEnabled = true;

    if (node.hasTypicalOf && !this.isAllTypicalOffExpanded) {
      const parentNode = this.findParentNode(node);
      const helperUniqueId = node.systemTypicalOfHelperUniqueId;
      const groupKey = helperUniqueId || node.itemName;

      parentNode?.children?.forEach(child => {
        const childHelperUniqueId = child.systemTypicalOfHelperUniqueId;
        const childGroupKey = childHelperUniqueId || child.itemName;

        if (child.hasTypicalOf && childGroupKey === groupKey) {
          child.isItemVisible = node.isItemVisible;
          if (child.children && child.children.length > 0) {
            this.setChildrenVisibility(child.children, child.isItemVisible);
          }
        }
      });
    }

    this.itemVisibilityHandlerForDxr(node);

    // Sync root node visibility back to the SQWebProject object
    if (node === this.treeData[0]) {
      this.areasData.isItemVisible = node.isItemVisible;
      this.commonDataService.setRootNodeHidden(!node.isItemVisible);
    }

    if (node && node.children && node.children.length > 0) {
      this.setChildrenVisibility(node.children, node.isItemVisible);
    }

  }

  private itemVisibilityHandlerForDxr(node: ProjectTreeNode): void {
    const helperUniqueId = node.systemTypicalOfHelperUniqueId;
    const helper = helperUniqueId ? this.typicalOfHelpers[helperUniqueId] : undefined;

    if (!helper || !helper.typicalOfSystems || helper.typicalOfSystems.length === CONSTANTS.ZERO) {
      return;
    }

    if (node.hasTypicalOf && node.isSystemDXR && node.isMaster && !node.isItemVisible) {
      const visibleSystems = helper.typicalOfSystems.filter(system => system.isItemVisible);
      if (visibleSystems && visibleSystems.length > CONSTANTS.ZERO) {
        node.isMaster = false;
        visibleSystems[CONSTANTS.ZERO].isMaster = true;
      }
    }

    if (node.hasTypicalOf && node.isSystemDXR && node.isItemVisible) {
      const masterSystem = helper.typicalOfSystems.find(system => system.isMaster);
      if (masterSystem && !masterSystem.isItemVisible) {
        node.isMaster = true;
        masterSystem.isMaster = false;
      }
    }
  }

  private setChildrenVisibility(children: ProjectTreeNode[], isVisible: boolean) {
    children.forEach(child => {
      child.isItemVisible = isVisible;
      // Recursively apply to children
      if (child.children && child.children.length > 0) {
        this.setChildrenVisibility(child.children, isVisible);
      }
    });
  }

  expandTypicalOff() {
    this.isAllTypicalOffExpanded = true;
    this.treeData.forEach(node => {
      this.expandTypicalOffInNode(node);
    });
    this.applyInitialExpansionState()
  }
  private expandTypicalOffInNode(node: ProjectTreeNode) {
    if (node && node.hasTypicalOf) {
      node.isTypicalOffExpanded = true;
    }
    if (node && node.children) {
      node.children.forEach(child => this.expandTypicalOffInNode(child));
    }
  }

  collapseTypicalOff() {
    this.isAllTypicalOffExpanded = false;
    this.treeData.forEach(node => {
      this.collapseTypicalOffInNode(node);
    });
    this.applyInitialExpansionState()
  }

  private collapseTypicalOffInNode(node: ProjectTreeNode) {
    if (node && node.children) {
      const typicalGroups = new Map<string, ProjectTreeNode[]>();

      node.children.forEach(child => {
        if (!child.hasTypicalOf) {
          return;
        }

        const helperUniqueId = child.systemTypicalOfHelperUniqueId;
        const groupKey = helperUniqueId || child.itemName;
        const group = typicalGroups.get(groupKey) ?? [];
        group.push(child);
        typicalGroups.set(groupKey, group);
      });

      typicalGroups.forEach(group => {
        group.forEach((item, index) => {
          item.isTypicalOffExpanded = index === 0;
        });
      });

      node.children.forEach(child => {
        if (!child.hasTypicalOf) {
          child.isTypicalOffExpanded = true;
        }
      });

      node.children.forEach(child => this.collapseTypicalOffInNode(child));
    }
  }

  expandAllNodes() {
    this.treeData.forEach(node => {
      this.expandAllNodesRecursive(node);
    });
  }

  private expandAllNodesRecursive(node: ProjectTreeNode) {
    if (node && node.children && node.children.length > 0) {
      node.isExpanded = true;
      this.projectTree.expand(node);
      node.children.forEach(child => this.expandAllNodesRecursive(child));
    }
  }

  collapseAllNodes() {
    this.treeData.forEach(node => {
      this.collapseAllNodesRecursive(node);
    });
  }

  private collapseAllNodesRecursive(node: ProjectTreeNode) {
    if (node && node.children && node.children.length > 0) {
      node.isExpanded = false;
      this.projectTree.collapse(node);
      node.children.forEach(child => this.collapseAllNodesRecursive(child));
    }
  }

  private findParentNode(targetNode: ProjectTreeNode): ProjectTreeNode | null {
    for (const rootNode of this.treeData) {
      const parent = this.findParentNodeRecursive(rootNode, targetNode);
      if (parent) {
        return parent;
      }
    }
    return null;
  }

  private findParentNodeRecursive(currentNode: ProjectTreeNode, targetNode: ProjectTreeNode): ProjectTreeNode | null {
    if (currentNode && currentNode.children) {
      for (const child of currentNode.children) {
        if (child === targetNode) {
          return currentNode;
        }
        const result = this.findParentNodeRecursive(child, targetNode);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  // Method to set custom column widths
  setColumnWidths(nameWidth: number, statusWidth: number, actionsWidth: number) {
    this.nameColumnWidth = `${nameWidth}%`;
    this.statusColumnWidth = `${statusWidth}%`;
    this.actionsColumnWidth = `${actionsWidth}%`;
  }

  ngAfterViewChecked() {
    if (this.currentlyEditingNode && this.nodeInputs.length > 0) {
      const inputElement = this.nodeInputs.first.nativeElement;
      if (document.activeElement !== inputElement) {
        inputElement.focus();
        inputElement.select();
      }
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent) {
    if (!this.currentlyEditingNode) {
      return;
    }
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT') return;
    this.currentlyEditingNode.isInEditMode = false;
    this.currentlyEditingNode = null;
  }

  ngOnDestroy() {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }
}
